import { createBrowserRouter, RouterProvider } from 'react-router-dom'

import AuthLayout from 'layouts/auth-layout/auth-layout'
import RootLayout from 'routers/RootLayout'

import LoginPage from 'pages/auth/login/login.page'
import SignupPage from 'pages/auth/signup/signup.page'
import ResetPasswordPage from 'pages/auth/reset-password/reset-password.page'
import DispatchSetPasswordPage from 'pages/auth/dispatch-set-password/dispatch-set-password.page'

import HomePage from 'pages/home/home.page'
import OrderPage from 'pages/order/order.page'
import TripPage from 'pages/trip/trip.page'
import InvoicePage from 'pages/invoice/invoice.page'
import AddressPage from 'pages/address/address.page'
import EquipmentPage from 'pages/equipment/equipment.page'
import DriverPage from 'pages/driver/driver.page'
import IntegrationPage from 'pages/integration/integration.page'
import CommissionPage from 'pages/commission/commission.page'
import SettingsPage from 'pages/settings/settings.page'
import CnAutomationRoute from 'pages/cn-automation/cn-automation-route'
import VerifyOrderPage from 'pages/verify-order/verify-order.page'
import CreateTripPage from 'pages/trip/create-trip/create-trip.page'
import CarrierTripPage from 'pages/carrier-trip/carrier-trip.page'
import DeleteDriverPage from 'pages/delete-driver/delete-driver.page'
import DeleteWebUserPage from 'pages/delete-web-user/delete-web-user.page'
import PrivacyPage from 'pages/privacy/privacy.page'
import AppDownloadPage from 'pages/app-download/app-download.page'
import LandingPage from 'pages/landing/landing.page'
import PortalPage from 'pages/portal/portal.page'
import SocialMediasPage from 'pages/social-medias/social-medias.page'
import PayablesPage from 'pages/payables/payables.page'
import CarrierPayablesPage from 'pages/payables/carrier-payables.page'
import DriverPayablesPage from 'pages/payables/driver-payables.page'
import PayableViewPage from 'pages/payables/payable-view/payable-view.page'
import ErrorPage from './ErrorPage'

// Create once at module scope. Building the router inside the component
// leaves extra history listeners after StrictMode remounts / HMR, so the
// URL can change while the visible page stays on the previous route.
const router = createBrowserRouter(
    [
      {
        path: 'delete',
        element: <DeleteDriverPage />,
      },
      {
        path: 'webdelete',
        element: <DeleteWebUserPage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPage />,
      },
      {
        // Public social links hub — no auth required.
        path: 'social',
        element: <SocialMediasPage />,
      },
      {
        // Driver app download — public store redirect (iOS / Android / desktop chooser).
        path: 'app',
        element: <AppDownloadPage />,
      },
      {
        // Customer portal — standalone, no operator layout / no operator auth required.
        // The page itself reads ?t= and exchanges it for a portal-domain JWT.
        path: 'portal',
        element: <PortalPage />,
      },
      {
        path: 'auth',
        element: <AuthLayout />,
        children: [
          {
            path: 'login',
            element: <LoginPage />,
          },
          {
            path: 'signup',
            element: <SignupPage />,
          },
          {
            path: 'reset-password',
            element: <ResetPasswordPage />,
          },
          {
            path: 'dispatch-set-password',
            element: <DispatchSetPasswordPage />,
          },
        ],
      },
      {
        path: '/',
        element: <RootLayout />,
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            element: <LandingPage />,
          },
          {
            path: 'home',
            element: <HomePage />,
          },
          {
            path: 'order',
            element: <OrderPage />,
            children: [
              {
                path: 'verify/:orderId',
                element: <VerifyOrderPage />,
              },
            ],
          },
          {
            path: 'trip',
            children: [
              {
                path: '',
                element: <TripPage />,
              },
              {
                path: 'create',
                element: <CreateTripPage />,
              },
            ],
          },
          {
            path: 'carrier-trip',
            element: <CarrierTripPage />,
          },
          {
            path: 'invoice',
            children: [
              {
                index: true,
                element: <InvoicePage />,
              },
              {
                path: ':invoiceId',
                element: <InvoicePage />,
              },
            ],
          },
          {
            path: 'factoring',
            children: [
              {
                index: true,
                element: <InvoicePage />,
              },
              {
                path: ':invoiceId',
                element: <InvoicePage />,
              },
            ],
          },
          {
            path: 'carrier',
            element: <CarrierPayablesPage />,
          },
          {
            path: 'vendor',
            element: <PayablesPage />,
            children: [
              {
                path: ':expenseId',
                element: <PayableViewPage />,
              },
            ],
          },
          {
            path: 'driver-payables',
            element: <DriverPayablesPage />,
          },
          {
            path: 'address',
            element: <AddressPage />,
          },
          {
            path: 'equipment',
            element: <EquipmentPage />,
          },
          {
            path: 'driver',
            element: <DriverPage />,
          },
          {
            path: 'integration',
            element: <IntegrationPage />,
          },
          {
            path: 'commission',
            element: <CommissionPage />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
        ],
      },
      {
        path: 'cn_automation',
        element: <CnAutomationRoute />,
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true,
      },
    }
  )

const MainRouter = () => {
  return (
    <RouterProvider
      router={router}
      future={{
        v7_startTransition: true,
      }}
    />
  )
}

export default MainRouter
